import { adminFetch } from './api';

/**
 * Admin Database Proxy Client
 * 
 * Mirrors the Supabase client API but routes all calls through
 * the server's /api/admin/db endpoint instead of direct Supabase access.
 * 
 * Usage (drop-in replacement for supabase.from()):
 *   const { data, error } = await adminDb.from('users').select('*').eq('id', userId);
 */

interface Filter {
  method: string;
  args: unknown[];
}

interface QueryParams {
  select?: string;
  options?: Record<string, unknown>;
  filters?: Filter[];
  order?: { column: string; options?: Record<string, unknown> };
  limit?: number;
  range?: [number, number];
  single?: boolean;
  data?: unknown;
}

class AdminQueryBuilder {
  private table: string;
  private operation: string;
  private params: QueryParams = {};

  constructor(table: string, operation: string) {
    this.table = table;
    this.operation = operation;
  }

  // Filter methods
  eq(column: string, value: unknown) {
    this.addFilter('eq', [column, value]);
    return this;
  }
  neq(column: string, value: unknown) {
    this.addFilter('neq', [column, value]);
    return this;
  }
  gt(column: string, value: unknown) {
    this.addFilter('gt', [column, value]);
    return this;
  }
  gte(column: string, value: unknown) {
    this.addFilter('gte', [column, value]);
    return this;
  }
  lt(column: string, value: unknown) {
    this.addFilter('lt', [column, value]);
    return this;
  }
  lte(column: string, value: unknown) {
    this.addFilter('lte', [column, value]);
    return this;
  }
  like(column: string, pattern: string) {
    this.addFilter('like', [column, pattern]);
    return this;
  }
  ilike(column: string, pattern: string) {
    this.addFilter('ilike', [column, pattern]);
    return this;
  }
  is(column: string, value: unknown) {
    this.addFilter('is', [column, value]);
    return this;
  }
  in(column: string, values: unknown[]) {
    this.addFilter('in', [column, values]);
    return this;
  }
  not(column: string, operator: string, value: unknown) {
    this.addFilter('not', [column, operator, value]);
    return this;
  }
  or(filters: string) {
    this.addFilter('or', [filters]);
    return this;
  }
  contains(column: string, value: unknown) {
    this.addFilter('contains', [column, value]);
    return this;
  }

  // Select with columns
  select(columns?: string, options?: Record<string, unknown>) {
    this.params.select = columns || '*';
    if (options) this.params.options = options;
    return this;
  }

  // Ordering
  order(column: string, options?: { ascending?: boolean }) {
    this.params.order = { column, options };
    return this;
  }

  // Pagination
  limit(count: number) {
    this.params.limit = count;
    return this;
  }
  range(from: number, to: number) {
    this.params.range = [from, to];
    return this;
  }

  // Single row
  single() {
    this.params.single = true;
    return this.execute();
  }

  // Collect (same as await — but kept for compatibility)
  maybeSingle() {
    this.params.single = true;
    return this.execute();
  }

  private addFilter(method: string, args: unknown[]) {
    if (!this.params.filters) this.params.filters = [];
    this.params.filters.push({ method, args });
  }

  // Execute the query via admin proxy
  async execute(): Promise<{ data: any; error: any; count?: number }> {
    try {
      const resp = await adminFetch('/api/admin/db', {
        method: 'POST',
        body: JSON.stringify({
          table: this.table,
          operation: this.operation,
          params: this.params,
        }),
      });
      const json = await resp.json();
      if (!resp.ok || json.error) {
        return { data: null, error: { message: json.error || `HTTP ${resp.status}` } };
      }
      return { data: json.data, error: null, count: json.count };
    } catch (e: any) {
      return { data: null, error: { message: e.message || 'Network error' } };
    }
  }

  // Make it thenable so you can await the builder directly
  then(resolve: (value: any) => void, reject?: (reason: any) => void) {
    return this.execute().then(resolve, reject);
  }
}

class AdminTableRef {
  private table: string;

  constructor(table: string) {
    this.table = table;
  }

  select(columns?: string, options?: Record<string, unknown>) {
    const builder = new AdminQueryBuilder(this.table, 'select');
    return builder.select(columns, options);
  }

  insert(data: unknown) {
    const builder = new AdminQueryBuilder(this.table, 'insert');
    builder['params'] = { ...builder['params'], data };
    return builder;
  }

  update(data: unknown) {
    const builder = new AdminQueryBuilder(this.table, 'update');
    builder['params'] = { ...builder['params'], data };
    return builder;
  }

  upsert(data: unknown, options?: Record<string, unknown>) {
    const builder = new AdminQueryBuilder(this.table, 'upsert');
    builder['params'] = { ...builder['params'], data, options };
    return builder;
  }

  delete() {
    return new AdminQueryBuilder(this.table, 'delete');
  }
}

export const adminDb = {
  from(table: string) {
    return new AdminTableRef(table);
  },
  async rpc(fn: string, args?: Record<string, unknown>) {
    try {
      const resp = await adminFetch('/api/admin/db', {
        method: 'POST',
        body: JSON.stringify({ table: fn, operation: 'rpc', params: { args } }),
      });
      const json = await resp.json();
      if (!resp.ok || json.error) {
        return { data: null, error: { message: json.error } };
      }
      return { data: json.data, error: null };
    } catch (e: any) {
      return { data: null, error: { message: e.message } };
    }
  },
};
